import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft, ArrowUpRight, BrainCircuit, Building2, Clock, Database,
  MapPin, Radio, Store, TriangleAlert,
} from "lucide-react";
import { api } from "../lib/api";
import SignalCard from "../components/SignalCard";
import { formatGeneratedAt } from "../lib/datetime";
import { InsightOpportunityForm } from "../components/InsightOpportunityForm";

const layerColor = {
  category: "text-bm-cyan border-bm-cyan/50",
  behaviour: "text-bm-amber border-bm-amber/50",
  product: "text-bm-green border-bm-green/50",
};

const DetailList = ({ label, icon: Icon, values, testId, color = "text-bm-cyan border-bm-cyan/30" }) => (
  <section className="border-t border-white/10 pt-4" data-testid={testId}>
    <div className="flex items-center gap-1.5 label-mono text-bm-dim mb-2">
      <Icon className="w-3.5 h-3.5" />{label}
    </div>
    {values?.length ? (
      <div className="flex flex-wrap gap-2">
        {values.map((value) => <span key={value} className={`text-xs font-mono px-2 py-1 border break-words max-w-full ${color}`}>{value}</span>)}
      </div>
    ) : <span className="font-mono text-xs text-bm-dim">—</span>}
  </section>
);

export default function InsightDetail() {
  const { id } = useParams();
  const [insight, setInsight] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    setInsight(null);
    setError("");
    api.insight(id)
      .then((data) => active && setInsight(data))
      .catch((requestError) => {
        if (active) setError(requestError.response?.status === 404 ? "INSIGHT NOT FOUND" : "INSIGHT COULD NOT BE LOADED");
      });
    return () => { active = false; };
  }, [id]);

  if (error) {
    return (
      <div className="max-w-6xl mx-auto space-y-6" data-testid="insight-detail-error">
        <Link to="/insights" className="inline-flex items-center gap-2 label-mono text-bm-muted hover:text-white" data-testid="back-link-error">
          <ArrowLeft className="w-3 h-3" />BACK TO INSIGHTS
        </Link>
        <div className="border border-bm-danger/40 p-8 font-mono text-sm text-bm-danger">{error}</div>
      </div>
    );
  }

  if (!insight) return <div className="font-mono text-xs text-bm-cyan scanline p-6" data-testid="insight-detail-loading">LOADING INSIGHT...</div>;

  const signals = insight.signals || [];
  const method = insight.generation_method === "ai_synthesis" ? "AI SYNTHESIS" : "CURATED";

  return (
    <main className="space-y-8 max-w-6xl mx-auto" data-testid="insight-detail">
      <Link to="/insights" className="inline-flex items-center gap-2 label-mono text-bm-muted hover:text-white" data-testid="back-link">
        <ArrowLeft className="w-3 h-3" />BACK TO INSIGHTS
      </Link>

      <header className="bm-card p-6 sm:p-8 fade-up">
        <div className="space-y-5 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`label-mono border px-2 py-1 ${layerColor[insight.layer] || "text-white border-white/30"}`}>{insight.layer}</span>
            <span className="label-mono text-bm-dim flex items-center gap-1"><MapPin className="w-3 h-3" />{insight.region}</span>
            <span className="label-mono text-bm-dim flex items-center gap-1"><BrainCircuit className="w-3 h-3" />{method}</span>
          </div>
          <div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black leading-none break-words" data-testid="insight-title">{insight.name}</h1>
            <p className="text-base text-bm-muted leading-relaxed mt-5 max-w-3xl" data-testid="insight-description">{insight.description}</p>
          </div>
          <div className="flex flex-wrap gap-x-5 gap-y-2 text-xs font-mono text-bm-dim">
            <span className="flex items-center gap-1.5" data-testid="insight-generated-at"><Clock className="w-3.5 h-3.5" />Generated {formatGeneratedAt(insight.generated_at || insight.created_at)}</span>
            <span className="flex items-center gap-1.5" data-testid="insight-source-count"><Database className="w-3.5 h-3.5" />{signals.length} verified source signal{signals.length === 1 ? "" : "s"}</span>
          </div>
        </div>
      </header>

      <section className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="insight-market-context">
        <DetailList label="CHANNELS" icon={Radio} values={insight.platforms} testId="insight-platforms" />
        <DetailList label="REFERENCE BRANDS" icon={Building2} values={insight.reference_brands} testId="insight-reference-brands" color="text-bm-amber border-bm-amber/30" />
        <DetailList label="WHERE IT IS WINNING" icon={Store} values={insight.winning_sellers} testId="insight-winning-sellers" color="text-bm-green border-bm-green/30" />
      </section>

      <section className="border-y border-white/10 py-4 grid grid-cols-1 sm:grid-cols-3 gap-4 font-mono text-xs" data-testid="insight-provenance">
        <div><div className="label-mono text-bm-dim mb-1">GENERATION METHOD</div><span className="text-white">{method}</span></div>
        <div><div className="label-mono text-bm-dim mb-1">MODEL</div><span className="text-white break-words">{insight.synthesis_model || "—"}</span></div>
        <div><div className="label-mono text-bm-dim mb-1">TRIGGER</div><span className="text-white break-words">{insight.source_trigger || "—"}</span></div>
      </section>

      <InsightOpportunityForm insight={insight} signals={signals} />

      <section className="space-y-4" data-testid="contributing-signals-section">
        <div className="flex items-end justify-between gap-4 border-b border-white/10 pb-3">
          <div>
            <div className="label-mono text-bm-amber">EVIDENCE CHAIN</div>
            <h2 className="font-heading text-2xl font-bold mt-1">Contributing signals</h2>
          </div>
          <div className="label-mono text-bm-green" data-testid="contributing-signals-count">{signals.length} VERIFIED</div>
        </div>

        {signals.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="contributing-signals-grid">
            {signals.map((signal, index) => (
              <Link
                key={signal.id}
                to={`/signals?title=${encodeURIComponent(signal.title)}`}
                aria-label={`Open source signal: ${signal.title}`}
                className="block focus-visible:outline focus-visible:outline-2 focus-visible:outline-bm-cyan"
                data-testid={`contributing-signal-link-${signal.id}`}
              >
                <SignalCard signal={signal} idx={index} expanded />
                <div className="flex justify-end items-center gap-1 label-mono text-bm-cyan mt-2"><span>OPEN IN SIGNAL EXPLORER</span><ArrowUpRight className="w-3 h-3" /></div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="bm-card border-dashed border-white/20 p-8 text-center" data-testid="empty-signals">
            <Database className="w-5 h-5 text-bm-dim mx-auto mb-3" />
            <div className="font-mono text-sm text-bm-dim">NO SIGNAL EVIDENCE STORED FOR THIS INSIGHT</div>
            <p className="text-xs text-bm-dim mt-2">This legacy or curated Insight does not contain exact source links. No inferred matches are shown.</p>
          </div>
        )}

        {insight.missing_signal_ids?.length > 0 && (
          <div className="border border-bm-amber/30 p-3 flex items-start gap-2 text-xs font-mono text-bm-muted" data-testid="missing-signal-warning">
            <TriangleAlert className="w-4 h-4 text-bm-amber shrink-0" />
            {insight.missing_signal_ids.length} stored source reference{insight.missing_signal_ids.length === 1 ? " is" : "s are"} no longer present in the Signal corpus.
          </div>
        )}
      </section>
    </main>
  );
}